import {
  dedupeArrayIndexRegEx,
  dedupeBlacklistedKeys,
  dedupeCsvKeys,
  dedupeFilterKeys,
  dedupeRegexFilterTag,
  dedupeSubstringKeys
} from "./dedupe.constants";
import { devLogger } from "@certa/common/utils/devLogger.ts";

/**
 * Replaces tags within the JSON with the same tags suffixed with uuid.
 * TODO: This function removes ids from the components. Check later
 * @param JSONString
 */
export const dedupeComponent = ({
  jsonString,
  tagMappings,
  externalTagMappings,
  idMappings
}: {
  jsonString: string;
  tagMappings: Record<string, string>;
  externalTagMappings: Record<string, string>;
  idMappings: Record<string, string>;
}) => {
  try {
    const originalTags = Object.keys(tagMappings);
    const originalIds = Object.keys(idMappings);
    const dedupedJSON = JSON.parse(jsonString, (key, value) => {
      /*!SECTION
  
        field_tags: [
          "my_field_tag" // Child workflow
        ]
  
        filters: ['fields.childField==$currentWFField']
        */
      /**
       * We're skipping checking for individual values if the key is of
       * numeric type. This is because we get key as 0 and value as something,
       * but here we don't know what the actual array's key is and therefore
       * in certain scenarios they shouldn't be replaces.
       *
       * Which is why we allow values where keys are numeric values,
       * but handle them here (which comes AFTER individual elements are
       * already have been iterated through).
       *
       * Here we can check whether or not we should replace these values or not.
       */
      if (Array.isArray(value) && !dedupeBlacklistedKeys.includes(key)) {
        if (dedupeFilterKeys.includes(key)) {
          // Case: Special handling for keys that may contain tags
          // as templates but prefixed with $

          return value.map(value => {
            if (typeof value !== "string") return value;
            let newValue = value;
            originalTags.forEach(tag => {
              newValue = newValue.replace(
                dedupeRegexFilterTag(tag),
                `$${tagMappings[tag]}`
              );
            });

            return newValue;
          });
        } else {
          return value.map(value =>
            typeof value === "string"
              ? tagMappings[value] || idMappings[value] || value
              : value
          );
        }
      }

      if (
        typeof value !== "string" ||
        dedupeBlacklistedKeys.includes(key) ||
        dedupeArrayIndexRegEx.test(key)
      )
        return value;

      if (key === "id" || originalIds.includes(value)) {
        return idMappings[value];
      }
      if (key === "externalTag") {
        // Case 1: For external Tag
        return externalTagMappings[value];
      }

      // Case 2: Original tag is a value, so we directly replace it
      else if (originalTags.includes(value)) {
        return tagMappings[value];
      }
      // Case 3: Special handing for keys that contain CSV values
      // so multiple tags may be present within a single comma separated
      // string
      else if (dedupeCsvKeys.includes(key)) {
        return value
          .split(",")
          .map((v: string) => tagMappings[v])
          .join(",");
      }
      // Case 4: Special handling for keys that may contain tags
      // as templates
      else if (dedupeSubstringKeys.includes(key)) {
        let newValue = value;

        originalTags.forEach(tag => {
          newValue = newValue.replaceAll(`{${tag}}`, `{${tagMappings[tag]}}`);
        });
        return newValue;
      }
      // Case 5: Return the value as it is
      else {
        return value;
      }
    });

    return dedupedJSON;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("Error parsing JSON", jsonString);
    devLogger.log(e);
    return {};
  }
};
